import { makeStyles } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { YesNoInput } from "components/inputs";
import { SelectEnumInput } from "components/inputs/SelectEnumInput";
import { outputDocumentsMapperTypes } from "entities/MapperType";
import { SplitOrdersType } from "entities/SplitOrdersType";
import { values } from "lodash";
import React from "react";
import {
	ArrayInput,
	BooleanInput,
	FormDataConsumer,
	SimpleFormIterator,
	TextInput,
	required,
	useTranslate,
} from "react-admin";
import { useRecordContext } from "react-admin";
import { useFormState } from "react-final-form";

import SimpleFormIteratorEx from "../../../components/_extensions/react-admin/ra-ui-materialui/src/form/SimpleFormIteratorEx";
import { validateUnique } from "../../../utils/validation/general";
import { CustomFieldInfoDialog } from "./CustomFieldInfoDialog";
import { MapperInfoButton } from "./MapperInfoButton";

const useArrayInputStyle = makeStyles(() => ({
	root: {
		"& div.ra-input": {
			display: "inline-block",
			marginLeft: "0.7em",
		},
		"& .MuiFormControl-root": {
			verticalAlign: "middle !important",
		},
	},
}));

const validateMapperCustomValue = (value, values) => {
	const result = values.data.find((item) => item.value === value);
	if (result?.key === "custom" && value) {
		const pattern =
			/^\s*([^\s:,][^:,]*\s*:\s*[^\s:,][^:,]*\s*:\s*[^\s:,][^:,]*)(\s*,\s*[^\s:,][^:,]*\s*:\s*[^\s:,][^:,]*\s*:\s*[^\s:,][^:,]*)*\s*$/;
		if (!pattern.test(value)) {
			return "osfc.messages.validation.validateMapperCustomValue";
		}
	}

	const allowedTypes = ["int", "str", "float", "datetime"];
	if (value) {
		const entries = value.split(",").map((entry) => entry.trim());
		if (result.key === "custom" && value) {
			for (const entry of entries) {
				const parts = entry.split(":").map((p) => p.trim());

				const type = parts[1];
				if (!allowedTypes.includes(type)) {
					return "osfc.messages.validation.invalidType";
				}
			}
		}
	}
};

export const MapperDataInput = (props) => {
	const arrayInputClasses = useArrayInputStyle();

	return (
		<>
			<ArrayInput {...props}>
				<SimpleFormIteratorEx classes={arrayInputClasses}>
					<TextInput
						source="key"
						label="resources.mappers.fields.data_field.key"
						validate={[required(), validateUnique("key")]}
					/>
					<TextInput
						source="value"
						label="resources.mappers.fields.data_field.value"
						validate={required()}
					/>
				</SimpleFormIteratorEx>
			</ArrayInput>
		</>
	);
};

export const MapperDataInputPredefined = ({
	requiredKeys,
	formData,
	orderImportCustomFields,
	...rest
}) => {
	const [isInfoDialogOpen, setIsInfoDialogOpen] = React.useState(false);
	const translate = useTranslate();
	const arrayInputClasses = useArrayInputStyle();
	const showInfoCustomField = (key) =>
		key === "custom" && outputDocumentsMapperTypes.includes(formData["type"]);

	const getValidator = (record, member, index, input, index2) => {
		if (
			input.props.source === "value" &&
			requiredKeys.indexOf(formData["data"][index]["key"]) !== -1
		)
			return required();
	};

	const infoDialogToggle = () => {
		setIsInfoDialogOpen((prev) => !prev);
	};

	return (
		<>
			<ArrayInput {...rest}>
				<SimpleFormIteratorEx
					classes={arrayInputClasses}
					disableAdd
					disableRemove
					disableReordering
					getValidator={requiredKeys && getValidator}
				>
					<TextInput
						style={{ width: "350px" }}
						source="key"
						label="resources.mappers.fields.data_field.key"
						validate={[required(), validateUnique("key")]}
						disabled
						format={(val) => {
							if (!val) return "";

							const field = orderImportCustomFields.find((f) => f.key === val);
							return field ? field.name : translate(`resources.mappers.fields.data_field.${val}`);
						}}
						parse={(v) => (v === undefined ? "" : v)}
					/>
					<TextInput
						style={{ width: "350px" }}
						source="value"
						label="resources.mappers.fields.data_field.value"
						parse={(v) => (v === undefined ? "" : v)}
						validate={validateMapperCustomValue}
					/>
					<FormDataConsumer>
						{({ scopedFormData }) =>
							showInfoCustomField(scopedFormData?.key) && (
								<Box display={"flex"}>
									<MapperInfoButton
										title={translate("osfc.buttons.info")}
										onClick={infoDialogToggle}
									/>
								</Box>
							)
						}
					</FormDataConsumer>
				</SimpleFormIteratorEx>
			</ArrayInput>
			{isInfoDialogOpen && (
				<CustomFieldInfoDialog translate={translate} onInfoDialogClose={infoDialogToggle} />
			)}
		</>
	);
};

export const MapperSettingsInputPredefined = ({ formData, form, ...rest }) => {
	const translate = useTranslate();
	const arrayInputClasses = useArrayInputStyle();

	return (
		<>
			<ArrayInput {...rest}>
				<SimpleFormIteratorEx
					classes={arrayInputClasses}
					disableAdd
					disableRemove
					disableReordering
				>
					<TextInput
						style={{ width: "300px", marginTop: "0px" }}
						source="key"
						label="resources.mappers.fields.settings_field.key"
						validate={[required(), validateUnique("key")]}
						disabled
						format={(val) => translate(`resources.mappers.fields.settings_field.${val}`)}
						parse={(v) => (v === undefined ? "" : v)}
					/>
					<FormDataConsumer>
						{({ scopedFormData, getSource }) => (
							<ValueFieldSwitcher
								currentSetting={scopedFormData}
								source={getSource("value")}
								values={formData}
								form={form}
							/>
						)}
					</FormDataConsumer>
					<BooleanInput
						source="is_show_on_import"
						label="resources.mappers.fields.settings_field.is_show_on_import"
						// helperText={translate(
						// 	"osfc.dialogs.orders-import-upload.fields.helper_text.split_document_content"
						// )}
						style={{ width: "150px", marginTop: "0px" }}
					/>
				</SimpleFormIteratorEx>
			</ArrayInput>
		</>
	);
};

const ValueFieldSwitcher = ({ currentSetting, source, values, form }) => {
	const translate = useTranslate();

	function updateSettings(settings, updates) {
		return settings.map((item) =>
			updates[item.key] !== undefined ? { ...item, value: updates[item.key] } : item
		);
	}

	const onSplitOrderChange = (e) => {
		if (e.target.value !== SplitOrdersType.not_split) {
			form.change(
				"settings",
				updateSettings(values.settings, {
					order_import_split_orders_type: e.target.value,
					is_order_import_add_loading_sequence_to_ref: true,
					is_order_import_split_document_content: true,
				})
			);
		} else {
			form.change(
				"settings",
				updateSettings(values.settings, {
					order_import_split_orders_type: e.target.value,
					is_order_import_add_loading_sequence_to_ref: false,
					is_order_import_split_document_content: false,
				})
			);
		}
	};
	const onAttachSourceDocChange = (e) => {
		if (!e.target.value) {
			form.change(
				"settings",
				updateSettings(values.settings, {
					is_order_import_attach_source_doc: e.target.value,
					is_order_import_split_document_content: false,
				})
			);
		} else {
			form.change(
				"settings",
				updateSettings(values.settings, {
					is_order_import_attach_source_doc: e.target.value,
					is_order_import_split_document_content: true,
				})
			);
		}
	};

	if (!currentSetting) return null;

	const key = currentSetting.key;

	switch (key) {
		case "order_import_split_orders_type":
			return (
				<SelectEnumInput
					source={source}
					label="resources.mappers.fields.settings_field.value"
					enumObj={SplitOrdersType}
					enumName="split-orders-type"
					helperText={translate(
						"osfc.dialogs.orders-import-upload.fields.helper_text.split_orders_type"
					)}
					onChange={onSplitOrderChange}
					style={{ width: "300px", marginTop: "0px" }}
				/>
			);

		case "is_order_import_add_loading_sequence_to_ref":
			return (
				<YesNoInput
					source={source}
					label="resources.mappers.fields.settings_field.value"
					helperText={translate(
						"osfc.dialogs.orders-import-upload.fields.helper_text.add_loading_sequence_to_ref"
					)}
					disabled={
						!values.settings?.find((i) => i.key === "order_import_split_orders_type")?.value
					}
					style={{ width: "300px", marginTop: "0px" }}
				/>
			);

		case "is_order_import_attach_source_doc":
			return (
				<YesNoInput
					source={source}
					label="resources.mappers.fields.settings_field.value"
					helperText={translate(
						"osfc.dialogs.orders-import-upload.fields.helper_text.attach_source_doc"
					)}
					onChange={onAttachSourceDocChange}
					style={{ width: "300px", marginTop: "0px" }}
				/>
			);

		case "is_order_import_generate_pallets":
			return (
				<YesNoInput
					source={source}
					label="resources.mappers.fields.settings_field.value"
					helperText={translate(
						"osfc.dialogs.orders-import-upload.fields.helper_text.should_generate_pallets"
					)}
					disabled={
						!values.settings?.find((i) => i.key === "is_order_import_attach_source_doc")?.value
					}
					style={{ width: "300px", marginTop: "0px" }}
				/>
			);

		case "is_order_import_split_document_content":
			return (
				<YesNoInput
					source={source}
					label="resources.mappers.fields.settings_field.value"
					helperText={translate(
						"osfc.dialogs.orders-import-upload.fields.helper_text.split_document_content"
					)}
					disabled={
						!values.settings?.find((i) => i.key === "is_order_import_attach_source_doc")?.value ||
						values.settings?.find((i) => i.key === "order_import_split_orders_type")?.value ===
							SplitOrdersType.not_split
					}
					style={{ width: "300px", marginTop: "0px" }}
				/>
			);
		default:
			return (
				<TextInput
					source={source}
					label="resources.mappers.fields.settings_field.value"
					style={{ width: "300px", marginTop: "0px" }}
				/>
			);
	}
};
